'use client'

/**
 * Stage 40 — the rows behind one evidence number, in the scenarios 6–7 drawer.
 *
 * Mounted only after the analyst opens the disclosure, so the drawer's first render issues
 * no request. The query is cached under (pair, evidence key): re-opening the same row, or
 * opening the same number quoted by a second reason card, does not re-fetch. React Query's
 * AbortSignal is threaded through, so closing the drawer cancels an in-flight request.
 *
 * The caption always names the FULL set. A truncated list is never allowed to read as the
 * whole history, and there is no «показать все» and no pagination: 50 rows is a sample for
 * checking, and the exact total lives in the sentence above it.
 *
 * When the server cannot prove that the enumerated rows are the set behind the number, it
 * returns `examples_unavailable` and this panel says so — it never falls back to rows that
 * merely look right.
 *
 * Stage 41: the rows are laid out as a table instead of one sentence per record. The panel
 * still owns nothing but the query and the states — the cells come from `toExampleCells`
 * and the layout from `evidence-history-table`, so both anomaly drawers show one shape.
 */
import { useState } from 'react'
import { useT, useFormat } from '@/lib/i18n'
import type { Formatter, TranslateFn } from '@/lib/i18n'

import { drilldownFootnoteText, type EvidenceDrilldown } from '../../evidence-row'
import { COMPARISON_BACKGROUND_NOTE } from '../../anomaly-semantics'
import { useEvidenceExamples } from '../cross-unit.hooks'
import { useCaseEvidenceExamplesV3 } from '../../anomalies-v3.hooks'
import {
  exampleCells,
  hasExactLegIdentity,
  sideCountsText,
  subjectText,
  variantGroups,
} from '../cross-unit.evidence-examples'
import {
  EvidenceExamplesTable,
  EvidenceTableShell,
  EvidenceVariantsTable,
} from '../../components/evidence-history-table'
import {
  HistoricalTransportDetail,
  type HistoryRowIdentity,
} from '../../components/historical-transport-detail'
import type { HistoryDetailContext } from '../../history-detail.types'
import { EvidenceRowDetail } from './evidence-row-detail'
import type { ExampleCells } from '../cross-unit.evidence-examples'

/**
 * Существительное подписи зависит от того, ЧТО за строка пришла, а не от панели.
 *
 * У сценариев 1–5 строка — СВЯЗКА «импорт → экспорт»: одна импортная и одна экспортная
 * запись корпуса, сопоставленные исторической логикой. Прежнее «перевозок» выдавало её
 * за подтверждённую перевозку, которой она не является. У 6–7 строка — ОДНА нога
 * корпуса: у одного номера отправки их десятки, и «перевозок» завышало смысл числа в
 * десятки раз. Панель одна на обе поверхности, поэтому выбор — по форме строк, и
 * терминология двух поверхностей намеренно РАЗНАЯ (см.
 * ai_transit/v2/reports/ANOMALY_EVIDENCE_TERMINOLOGY_AUDIT.md).
 */
const PAIR_NOUNS: [string, string, string] = ['связка', 'связки', 'связок']
const LEG_NOUNS: [string, string, string] = ['запись', 'записи', 'записей']

/** Русское склонение выбранной тройки форм. */
function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(Math.trunc(n)) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return forms[2]
  if (last === 1) return forms[0]
  if (last >= 2 && last <= 4) return forms[1]
  return forms[2]
}

function rowNouns(rows: ExampleCells[]): [string, string, string] {
  return rows.some((r) => r.side === 'pair') ? PAIR_NOUNS : LEG_NOUNS
}
const VARIANT_NOUNS: [string, string, string] = ['вариант', 'варианта', 'вариантов']

/**
 * Подпись под списком: сколько показано, чем отобрано и — если нужно — чего в колонках
 * не видно.
 *
 * `columnsNote` появляется там, где поле отбора отсутствует среди колонок примера
 * (популяция по коду груза импортной ноги, окна всплеска по всем физическим единицам).
 * Без него читатель видит в колонке «Груз» другое значение и делает вывод, что показаны
 * чужие строки. Ничего не фильтрует и не пересчитывает.
 */
function panelNote(
  d: EvidenceDrilldown,
  data: { returnedCount: number; selectionDescription: string | null },
  nouns: [string, string, string],
  /**
   * Сценарии 1–5: одна компактная строка о природе исторических связок.
   *
   * Стоит ЗДЕСЬ и только здесь — под самим списком, где читатель видит пару
   * «Плиты древесностружечные → Вагоны железнодорожные» и может прочесть её как
   * превращение груза. В карточках причин её нет намеренно: дисклеймер под каждым
   * числом перестают читать. На поверхности 6–7 не показывается — там строка не
   * пара, а одна нога корпуса, и это пояснение было бы неверным.
   */
  pairCorpusNote: boolean | undefined,
  t: TranslateFn,
  f: Formatter,
): string {
  const noun = pluralRu(d.totalCount, nouns)
  return [
    drilldownFootnoteText(d, data.returnedCount, noun, t, f),
    data.selectionDescription ? t('analytics.evi.selection', { value: data.selectionDescription }) : null,
    d.columnsNote,
    pairCorpusNote ? COMPARISON_BACKGROUND_NOTE : null,
  ].filter(Boolean).join(' ')
}

/**
 * «Показано 50 из 92 179 · ТОО «Богатырь Комир» · БИН 970340000843 · Из них 45 030
 * импортных · 47 149 экспортных» — шапка над таблицей.
 *
 * Всегда называет ПОЛНЫЙ набор, и с Stage 43 — ещё и чей он и из чего состоит. Состав по
 * сторонам стоит здесь, а не в примечании внизу: читатель видит импортную строку под
 * причиной «участник экспортной отправки» в первую же секунду, и объяснение должно
 * встретиться ему раньше, чем вывод «показаны чужие строки».
 */
function shownSummary(
  total: number | null | undefined,
  returned: number,
  f: Formatter,
  extra: (string | null)[] = [],
): string {
  const n = (v: number) => f.int(v)
  const head = typeof total === 'number' && total > returned
    ? `Показано ${n(returned)} из ${n(total)}`
    : `Показано ${n(returned)} из ${n(returned)}`
  return [head, ...extra.filter(Boolean)].join(' · ')
}

/** Which surface the number came from — a candidate pair (6–7) or a case row (1–5). */
export type EvidenceExamplesSource =
  | { kind: 'pair'; pairId: string }
  | { kind: 'case'; caseId: string; scenarioId: number }

export function EvidenceExamplesPanel({
  pairId,
  source,
  drilldown,
}: {
  /** Scenarios 6–7 call site; kept so existing usages need no edit. */
  pairId?: string
  /** Scenarios 1–5 call site. Exactly one of `pairId` / `source` is given. */
  source?: EvidenceExamplesSource
  drilldown: EvidenceDrilldown
}) {
  const t = useT()
  const f = useFormat()
  const resolved: EvidenceExamplesSource | null =
    source ?? (pairId ? { kind: 'pair', pairId } : null)
  const isCase = resolved?.kind === 'case'

  // Both hooks are called unconditionally — only one is enabled. The panel exists only
  // once the row is open, so the enabled one fetches immediately and the other never does.
  const pairQuery = useEvidenceExamples(
    resolved?.kind === 'pair' ? resolved.pairId : null, drilldown.evidenceKey, !isCase)
  const caseQuery = useCaseEvidenceExamplesV3(
    resolved?.kind === 'case' ? resolved.caseId : null,
    resolved?.kind === 'case' ? resolved.scenarioId : null,
    drilldown.evidenceKey, isCase)
  const { data, isLoading, isError, error, refetch, isFetching } = isCase ? caseQuery : pairQuery

  /* Какая историческая связка раскрыта поверх карточки кейса.
   *
   * Состояние живёт здесь, а не в таблице: панель уже знает, из какого набора
   * доказательств открыта строка, и только она может ответить «почему эта связка
   * показана». Карточка кейса под ней не размонтируется, поэтому её прокрутка и
   * раскрытые блоки сохраняются сами. */
  const [opened, setOpened] = useState<
    { identity: HistoryRowIdentity; context: HistoryDetailContext } | null>(null)

  /* Какая ОДНОНОГАЯ запись 6–7 раскрыта. Отдельное состояние от парного: у строки 6–7
   * идентичность одна (`source_uid`), а не две, и подставить её в парную карточку
   * нельзя — той нужны обе ноги. */
  const [openedLeg, setOpenedLeg] = useState<
    { row: ExampleCells; context: HistoryDetailContext } | null>(null)

  /** Контекст показа — из метаданных ЭТОГО набора. Новой логики отбора здесь нет. */
  const contextOf = (variantLabel?: string | null): HistoryDetailContext => ({
    datasetType: data?.datasetType ?? drilldown.datasetType ?? null,
    title: data?.title ?? drilldown.title ?? null,
    selectionDescription: data?.selectionDescription ?? drilldown.selectionDescription ?? null,
    variantLabel: variantLabel ?? null,
  })

  const openRow = (row: ExampleCells, variantLabel?: string | null) => {
    // Одноногая строка 6–7 открывается своей карточкой и по своей идентичности.
    if (hasExactLegIdentity(row)) {
      setOpenedLeg({ row, context: contextOf(variantLabel) })
      return
    }
    if (!row.importRecordId || !row.exportRecordId) return
    setOpened({
      identity: {
        importRecordId: row.importRecordId,
        exportRecordId: row.exportRecordId,
        importShipment: row.importShipment,
        exportShipment: row.exportShipment,
      },
      context: contextOf(variantLabel),
    })
  }

  /** Карточка подробностей рисуется рядом с панелью и снимается по закрытию. */
  const detail = opened ? (
    <HistoricalTransportDetail
      identity={opened.identity}
      context={opened.context}
      onClose={() => setOpened(null)}
    />
  ) : openedLeg ? (
    <EvidenceRowDetail
      row={openedLeg.row}
      context={openedLeg.context}
      onClose={() => setOpenedLeg(null)}
    />
  ) : null

  if (isLoading) {
    return <p data-state="loading" className="text-[10px] text-[var(--text-tertiary)]">{t('analytics.evidence.loadingExamples')}</p>
  }

  if (isError) {
    return (
      <div data-state="error" className="space-y-1">
        <p className="text-[10px] text-[var(--text-tertiary)]">
          Не удалось загрузить примеры{error instanceof Error && error.message ? `: ${error.message}` : ''}.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-[3px] border border-[var(--border-default)] px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)] disabled:opacity-40"
        >
          {t('common.action.retry')}
        </button>
      </div>
    )
  }

  if (!data || data.status !== 'ok') {
    return (
      <p data-state="unavailable" className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">
        {t('analytics.evi.examplesUnverified')}
      </p>
    )
  }

  // «Варианты комбинации кодов груза» / «Другие грузы на вывоз» — набор вариантов,
  // каждый со своим полным счётчиком и коротким срезом связок внутри.
  const variants = variantGroups(data)
  if (variants.length > 0) {
    return (
      <div data-state="ready" data-variants={variants.length} data-total={data.totalCount ?? undefined}>
        <EvidenceTableShell
          summary={shownSummary(data.totalCount, data.returnedCount, f,
            [subjectText(data), sideCountsText(data)])}
          note={panelNote(drilldown, data, VARIANT_NOUNS, isCase, t, f)}
        >
          <EvidenceVariantsTable
            groups={variants}
            onOpenRow={(row, group) => openRow(row, group.keyLabel ?? group.groupKey)}
          />
        </EvidenceTableShell>
        {detail}
      </div>
    )
  }

  const rows = exampleCells(data)
  const nouns = rowNouns(rows)
  if (rows.length === 0) {
    return (
      <p data-state="empty" className="text-[10px] text-[var(--text-tertiary)]">
        {t('analytics.evi.noHistorical')}
      </p>
    )
  }

  return (
    <div data-state="ready" data-total={data.totalCount ?? undefined} data-returned={data.returnedCount}>
      <EvidenceTableShell
        summary={shownSummary(data.totalCount, data.returnedCount, f,
          [subjectText(data), sideCountsText(data)])}
        note={panelNote(drilldown, data, nouns, isCase && nouns === PAIR_NOUNS, t, f)}
      >
        <EvidenceExamplesTable rows={rows} onOpenRow={(row) => openRow(row)} />
      </EvidenceTableShell>
      {detail}
    </div>
  )
}
